/**
 * ActionBtn — icon-over-label control used in immersive-setup and onboard-test,
 * plus a compact inline variant for hero rows. Brand tokens only.
 *
 * `variant`:
 *  - "stack" (default): icon above label, square-ish control (live-test row)
 *  - "inline": icon beside label, pill (hero actions)
 * `emphasis`:
 *  - "default": white surface, border
 *  - "active": brand-tinted, primary text
 *  - "dark": dark surface, white text
 *  - "gradient": signature gradient fill, white text
 */
import type { LucideIcon } from "lucide-react";

type ActionVariant = "stack" | "inline";
type ActionEmphasis = "default" | "active" | "dark" | "gradient";

interface ActionBtnProps {
  icon: LucideIcon;
  label: string;
  variant?: ActionVariant;
  emphasis?: ActionEmphasis;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

export const ActionBtn = ({
  icon: Icon,
  label,
  variant = "stack",
  emphasis = "default",
  onClick,
  className,
  disabled,
}: ActionBtnProps) => {
  const isGradient = emphasis === "gradient";
  const isDark = emphasis === "dark";
  const isActive = emphasis === "active";

  const surface = isGradient
    ? "text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 border-transparent"
    : isDark
      ? "bg-gray-800 text-white border-transparent shadow-md"
      : isActive
        ? "bg-genie-primary/10 text-genie-primary border-genie-primary/30"
        : "bg-white text-[#222B45] border-gray-200";

  if (variant === "inline") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`inline-flex items-center gap-2 h-[42px] px-4 rounded-xl border text-[13.5px] font-bold transition-all hover:opacity-90 disabled:opacity-50 ${surface} ${className ?? ""}`}
      >
        <Icon className="w-4 h-4" />
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center justify-center gap-1.5 min-w-[72px] px-4 py-3 rounded-2xl border text-xs font-semibold transition-all hover:opacity-90 disabled:opacity-50 ${surface} ${className ?? ""}`}
    >
      <Icon className="w-5 h-5" />
      {label}
    </button>
  );
};

export default ActionBtn;
